import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router';
import { 
  CheckCircle2, 
  Copy, 
  Check, 
  Clock, 
  Truck, 
  Printer, 
  ArrowLeft
} from 'lucide-react';
import LojaService from '../../services/Loja/LojaService';
import NavBar from '../../components/layout/NavBar';
import Footer from '../../components/layout/Footer';

export default function LojaPedidoView() {
  const { numero } = useParams();
  const location = useLocation();

  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!order);
  const [copied, setCopied] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(order?.status || 'aguardando_pagamento');

  useEffect(() => {
    async function loadOrder() {
      if (!order && numero) {
        setLoading(true);
        const data = await LojaService.getOrder(numero);
        if (data) {
          setOrder(data);
          setCurrentStatus(data.status);
        }
        setLoading(false);
      }
    }
    loadOrder();
  }, [numero, order]);

  const steps = [
    { key: 'aguardando_pagamento', label: 'Aguardando Pagamento' },
    { key: 'pagamento_aprovado', label: 'Pagamento Aprovado' },
    { key: 'em_separacao', label: 'Em Separação' },
    { key: 'pronto_para_envio', label: 'Pronto para Envio' },
    { key: 'enviado', label: 'Enviado' },
    { key: 'entregue', label: 'Entregue' }
  ];

  const getStepIndex = (status) => {
    const idx = steps.findIndex(s => s.key === status);
    return idx >= 0 ? idx : 0;
  };

  const handleCopyPix = () => {
    if (order?.pix?.qrCodeText) {
      navigator.clipboard.writeText(order.pix.qrCodeText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleSimularPagamentoAprovado = () => {
    setCurrentStatus('pagamento_aprovado');
    if (order) {
      const updated = { ...order, status: 'pagamento_aprovado' };
      setOrder(updated);
      try {
        const recentOrders = JSON.parse(localStorage.getItem('urb_orders') || '[]');
        const next = recentOrders.map(o => o.orderNumber === order.orderNumber ? updated : o);
        localStorage.setItem('urb_orders', JSON.stringify(next));
      } catch (e) {}
    }
  };

  const activeIndex = getStepIndex(currentStatus);

  if (loading) {
    return (
      <div className="min-h-screen bg-urb-dark flex items-center justify-center text-white">
        <p className="text-sm font-bold animate-pulse">Carregando dados do pedido...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-urb-dark flex flex-col justify-between">
        <NavBar />
        <div className="max-w-md mx-auto text-center p-8 space-y-4">
          <h2 className="text-2xl font-bold text-white">Pedido não encontrado</h2>
          <p className="text-xs text-urb-text-secondary">
            Verifique o código ou acesse seu histórico de compras.
          </p>
          <Link
            to="/loja"
            className="inline-flex px-6 py-3 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase"
          >
            Voltar para a Loja
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-urb-dark flex flex-col selection:bg-urb-primary selection:text-black">
      <NavBar />

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8">
        
        {/* Topo: Parabéns e Código */}
        <div className="p-6 sm:p-8 rounded-3xl bg-urb-card border border-urb-border text-center space-y-4 relative overflow-hidden">
          <div className="w-16 h-16 rounded-full bg-urb-primary/10 border border-urb-primary/30 flex items-center justify-center mx-auto text-urb-primary">
            <CheckCircle2 className="w-8 h-8" />
          </div>

          <div>
            <span className="text-xs font-black uppercase text-urb-primary tracking-widest block mb-1">
              PEDIDO CONFIRMADO COM SUCESSO
            </span>
            <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight uppercase">
              {order.orderNumber}
            </h1>
            <p className="text-xs sm:text-sm text-urb-text-secondary mt-1">
              Obrigado, <strong className="text-white">{order.customer?.name}</strong>! Seu pedido foi registrado no sistema URBRACE.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-urb-dark border border-urb-border text-xs text-urb-text-muted">
            <Clock className="w-3.5 h-3.5 text-urb-primary" />
            <span>Realizado em {new Date(order.createdAt).toLocaleDateString('pt-BR')} às {new Date(order.createdAt).toLocaleTimeString('pt-BR')}</span>
          </div>
        </div>

        {/* Linha do Tempo Visual do Status */}
        <div className="p-6 sm:p-8 rounded-3xl bg-urb-card border border-urb-border space-y-6">
          <h3 className="text-sm font-black uppercase tracking-wider text-white">
            Status do Pedido em Tempo Real
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {steps.map((s, idx) => {
              const isDone = idx < activeIndex;
              const isCurrent = idx === activeIndex;

              return (
                <div 
                  key={s.key}
                  className={`p-3 rounded-2xl border flex flex-col items-center text-center gap-2 transition-all ${
                    isCurrent 
                      ? 'bg-urb-primary/10 border-urb-primary text-white shadow-[0_0_15px_rgba(138,230,0,0.2)]' 
                      : isDone
                      ? 'bg-urb-dark/80 border-urb-primary/30 text-urb-text-secondary'
                      : 'bg-urb-dark/30 border-urb-border/50 text-urb-text-muted opacity-50'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${
                    isDone 
                      ? 'bg-urb-primary text-black' 
                      : isCurrent 
                      ? 'bg-urb-primary text-black animate-pulse' 
                      : 'bg-urb-card text-urb-text-muted border border-urb-border'
                  }`}>
                    {isDone ? <Check className="w-4 h-4 stroke-[3]" /> : idx + 1}
                  </div>
                  <span className="text-[11px] font-bold leading-tight uppercase">
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>

          {order.trackingCode && (
            <div className="p-4 rounded-xl bg-urb-dark border border-urb-border flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-urb-primary font-bold">
                <Truck className="w-4 h-4" />
                <span>Código de Rastreamento:</span>
                <span className="font-mono text-white text-sm">{order.trackingCode}</span>
              </div>
            </div>
          )}
        </div>

        {/* Caixa de Pagamento PIX (Se aguardando pagamento) */}
        {currentStatus === 'aguardando_pagamento' && order.paymentMethod === 'PIX' && order.pix && (
          <div className="p-6 sm:p-8 rounded-3xl bg-urb-card border-2 border-urb-primary/40 text-center space-y-6 shadow-[0_0_30px_rgba(138,230,0,0.15)]">
            <div>
              <span className="px-3 py-1 rounded-full bg-urb-primary text-black font-black text-xs uppercase tracking-wider">
                PAGUE COM PIX PARA LIBERAÇÃO IMEDIATA
              </span>
              <h3 className="text-xl font-black text-white uppercase mt-2">
                Escaneie o QR Code ou Copie o Código
              </h3>
              <p className="text-xs text-urb-text-secondary mt-1">
                Abra o app do seu banco, escolha a opção PIX e aponte a câmera ou use o Copia e Cola.
              </p>
            </div>

            {/* Imagem do QR Code */}
            <div className="inline-block p-4 rounded-2xl bg-white border-2 border-urb-primary shadow-xl">
              <img
                src={order.pix.qrCodeUrl}
                alt="QR Code PIX"
                className="w-48 h-48 sm:w-56 sm:h-56 mx-auto object-contain"
              />
            </div>

            {/* Copia e Cola */}
            <div className="max-w-xl mx-auto flex items-center bg-urb-dark rounded-xl border border-urb-border overflow-hidden">
              <input
                type="text"
                readOnly
                value={order.pix.qrCodeText}
                className="flex-1 bg-transparent px-4 py-3 text-xs font-mono text-urb-text-secondary truncate focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopyPix}
                className="px-5 py-3 bg-urb-primary hover:bg-urb-primary-hover text-black font-extrabold text-xs uppercase flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copiar PIX</span>
                  </>
                )}
              </button>
            </div>

            {/* Botão de Demonstração / Simulação */}
            <div className="pt-2">
              <button
                type="button"
                onClick={handleSimularPagamentoAprovado}
                className="text-xs text-urb-primary hover:underline font-bold"
              >
                Simular confirmação bancária (aprovar agora)
              </button>
            </div>
          </div>
        )}

        {/* Resumo Completo do Pedido */}
        <div className="p-6 sm:p-8 rounded-3xl bg-urb-card border border-urb-border space-y-6">
          <div className="flex items-center justify-between border-b border-urb-border pb-4">
            <h3 className="text-base font-extrabold text-white uppercase">
              Itens do Pedido
            </h3>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 text-xs text-urb-text-muted hover:text-white font-bold cursor-pointer"
            >
              <Printer className="w-4 h-4" />
              <span>Imprimir Comprovante</span>
            </button>
          </div>

          {/* Lista dos Itens */}
          <div className="space-y-4">
            {order.items?.map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 py-2 border-b border-urb-border/40 last:border-0">
                <div className="w-14 h-14 rounded-xl bg-urb-dark p-1.5 border border-urb-border shrink-0 flex items-center justify-center">
                  <img src={item.image} alt={item.productName} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-white truncate">{item.productName}</h4>
                  <p className="text-xs text-urb-text-secondary mt-0.5">
                    Tamanho: <strong className="text-white">{item.size}</strong> • Cor: <strong className="text-white">{item.color}</strong>
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-xs text-urb-text-muted block">Qtd: {item.quantity}</span>
                  <span className="text-sm font-black text-white">
                    R$ {(item.price * item.quantity).toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Dados de Entrega e Pagamento */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-urb-border text-xs text-urb-text-secondary">
            <div className="p-4 rounded-xl bg-urb-dark border border-urb-border space-y-1">
              <span className="font-bold text-white block uppercase text-[11px] text-urb-primary">
                Endereço de Entrega
              </span>
              <p className="text-white font-medium">{order.shipping?.street}, {order.shipping?.number} {order.shipping?.complement}</p>
              <p>{order.shipping?.neighborhood} - {order.shipping?.city}/{order.shipping?.state}</p>
              <p>CEP: {order.shipping?.cep || order.shipping?.postalCode}</p>
              <p className="pt-1 text-urb-primary font-bold">Método: {order.shipping?.methodName}</p>
            </div>

            <div className="p-4 rounded-xl bg-urb-dark border border-urb-border space-y-1">
              <span className="font-bold text-white block uppercase text-[11px] text-urb-primary">
                Totais Finais
              </span>
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="text-white">R$ {order.subtotal?.toFixed(2).replace('.', ',')}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-urb-primary">
                  <span>Desconto:</span>
                  <span>- R$ {order.discount?.toFixed(2).replace('.', ',')}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Frete:</span>
                <span className="text-white">R$ {order.shippingCost?.toFixed(2).replace('.', ',')}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-urb-border text-sm font-black text-white">
                <span>Total:</span>
                <span className="text-urb-primary">R$ {order.totalAmount?.toFixed(2).replace('.', ',')}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Botão de Retorno */}
        <div className="text-center">
          <Link
            to="/loja"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-urb-primary text-black font-extrabold text-xs uppercase tracking-wider hover:bg-urb-primary-hover transition-all"
          >
            <ArrowLeft className="w-4 h-4 stroke-[3]" />
            <span>CONTINUAR COMPRANDO NA LOJA URB</span>
          </Link>
        </div>

      </main>

      <Footer />
    </div>
  );
}
